<template>
  <BaseDialog centered contentClass="final-decision" ref="dialog" persistent :show-actions="true">
    <template slot="title">
      <div class="final-decision-dialog__title">
        {{
          isChange ? $t('go-nogo.final-decision.dialog.change-title') : $t('go-nogo.final-decision.dialog.record-title')
        }}
      </div>
    </template>
    <template slot="content">
      <!-- aria-pressed is bound as a STRING on purpose: Vue 2 drops an attribute bound to `false`,
           which would leave an unselected button with no toggle state to announce. -->
      <div class="base-dialog__content__choices">
        <button
          type="button"
          class="square-button go"
          :class="{ selected: selectedButton === 'go' }"
          :aria-pressed="String(selectedButton === 'go')"
          @click="selectButton('go')"
        >
          <span>{{ $t('go-nogo.dialog.go') }}</span>
        </button>
        <button
          type="button"
          class="square-button nogo"
          :class="{ selected: selectedButton === 'nogo' }"
          :aria-pressed="String(selectedButton === 'nogo')"
          @click="selectButton('nogo')"
        >
          <span>{{ $t('go-nogo.dialog.no-go') }}</span>
        </button>
      </div>
      <transition name="fade-in-slow">
        <div v-if="presetReasons.length" class="final-decision-dialog__reasons">
          <BaseChip
            v-for="reason in presetReasons"
            :key="reason.id"
            size="sm"
            :tone="getGoNoGoVoteTone(selectedButton)"
            :preset="isReasonSelected(reason) ? 'soft' : 'secondary'"
            :selected="isReasonSelected(reason)"
            @click="toggleReason(reason)"
          >
            {{ reason.reason }}
          </BaseChip>
        </div>
      </transition>
      <transition name="fade-in-slow">
        <div v-if="selectedButton" class="final-decision-dialog__comment">
          <p class="final-decision-dialog__comment-label">{{ $t('go-nogo.final-decision.dialog.comment-label') }}</p>
          <p class="final-decision-dialog__comment-hint">{{ $t('go-nogo.final-decision.dialog.comment-hint') }}</p>
          <FormInput
            is-textarea
            is-compact
            name="final-decision-comment"
            :rows="4"
            :value="commentValue"
            :placeholder="$t('go-nogo.final-decision.dialog.comment-placeholder')"
            @input="commentValue = $event"
          />
        </div>
      </transition>
    </template>
    <template slot="actions">
      <BaseVoteButton @click.native="cancelClick">
        {{ $t('actions.cancel') }}
      </BaseVoteButton>
      <BaseVoteButton
        :disabled="!selectedButton || !hasChanges || submitting"
        :is-loading="submitting"
        @click.native="execute"
      >
        {{ $t('go-nogo.final-decision.dialog.confirm') }}
      </BaseVoteButton>
    </template>
  </BaseDialog>
</template>

<script>
import BaseChip from '@/components/base/BaseChip.vue';
import BaseDialog from '@/components/base/BaseDialog';
import BaseVoteButton from '@/components/base/BaseVoteButton';
import FormInput from '@/components/form/FormInput';
import { getGoNoGoVoteTone } from '@/util/tag-tones/go-nogo-tag';
import { normalizeVoteReasonNewlines, sameVoteReasons } from '@/util/go-nogo-vote-reasons';
import { mapActions, mapGetters } from 'vuex';

const VOTE_BY_BUTTON = { go: 'YES', nogo: 'NO' };

/** `''` and `null` both mean "no comment", so they must compare equal. */
const comparableComment = (value) => normalizeVoteReasonNewlines(value).trim() || null;

/**
 * Unlike the secondary-vote dialog, reasons and comment are sent as two separate fields — the API
 * stores them apart, so there is nothing to partition on the way in or out.
 */
export default {
  name: 'FinalDecisionDialog',
  components: {
    BaseChip,
    BaseDialog,
    BaseVoteButton,
    FormInput,
  },
  props: {
    finalDecision: {
      type: Object,
      default: null,
    },
    submitting: Boolean,
  },
  data() {
    return {
      selectedButton: null,
      selectedReasons: [],
      commentValue: '',
    };
  },
  computed: {
    ...mapGetters('goNoGo', ['tenantOpportunityVoteReasons', 'voteReasonsLoaded']),
    isChange() {
      return !!this.finalDecision;
    },
    presetReasons() {
      if (!this.selectedButton) return [];
      return this.tenantOpportunityVoteReasons.filter(
        (reason) => reason.voteType === VOTE_BY_BUTTON[this.selectedButton],
      );
    },
    // PUT appends a journal event, so an untouched save would add an "old decision" entry that
    // documents nothing. Recording a first decision has nothing to compare against.
    hasChanges() {
      if (!this.finalDecision) return true;
      if (!this.selectedButton) return false;
      if (VOTE_BY_BUTTON[this.selectedButton] !== this.finalDecision.vote) return true;
      if (!sameVoteReasons(this.selectedReasons, this.finalDecision.reasons)) return true;
      return comparableComment(this.commentValue) !== comparableComment(this.finalDecision.comment);
    },
  },
  methods: {
    ...mapActions('goNoGo', ['getOpportunityVoteReasons']),
    getGoNoGoVoteTone,
    openDialog() {
      this.resetForm();
      // Not awaited: initialising doesn't depend on the presets, so the tag list just fills in.
      this.ensureVoteReasons();
      if (this.finalDecision) {
        this.initFromFinalDecision(this.finalDecision);
      }
      this.$refs.dialog.openModal();
    },
    // Owns the fetch: otherwise the presets arrive only as a side effect of `fetchOpportunityVotes`,
    // so a failed votes request would leave this dialog with no reason tags.
    ensureVoteReasons() {
      if (this.voteReasonsLoaded) return Promise.resolve();
      return this.getOpportunityVoteReasons();
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
      this.resetForm();
    },
    resetForm() {
      this.selectedButton = null;
      this.selectedReasons = [];
      this.commentValue = '';
    },
    initFromFinalDecision(finalDecision) {
      this.selectedButton = finalDecision.vote === 'YES' ? 'go' : finalDecision.vote === 'NO' ? 'nogo' : null;
      this.selectedReasons = [...(finalDecision.reasons || [])];
      this.commentValue = normalizeVoteReasonNewlines(finalDecision.comment);
    },
    selectButton(button) {
      if (this.selectedButton === button) {
        this.resetForm();
        return;
      }
      this.selectedButton = button;
      // Presets are scoped to a vote type, so a previous selection no longer applies.
      this.selectedReasons = [];
    },
    toggleReason(reason) {
      const index = this.selectedReasons.indexOf(reason.reason);
      if (index === -1) {
        this.selectedReasons.push(reason.reason);
      } else {
        this.selectedReasons.splice(index, 1);
      }
    },
    isReasonSelected(reason) {
      return this.selectedReasons.includes(reason.reason);
    },
    execute() {
      if (!this.selectedButton || !this.hasChanges) return;

      const comment = this.commentValue.trim();
      this.$emit('confirm', {
        vote: VOTE_BY_BUTTON[this.selectedButton],
        reasons: [...this.selectedReasons],
        comment: comment || null,
      });
    },
    cancelClick() {
      this.closeDialog();
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss">
.base-dialog__final-decision {
  min-height: max-content;
  width: 700px;
  position: relative;
  border-radius: $border-radius-root;
  height: auto !important;

  .base-dialog__content {
    background: $color-white !important;
    padding: 16px !important;

    &__choices {
      justify-content: stretch;
      display: flex;
      padding: 16px;
      flex-direction: row;
      gap: 16px;

      span {
        text-transform: uppercase;
      }
    }

    .square-button {
      color: grey;
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: 12px;
      white-space: nowrap;
      overflow: hidden;
      justify-content: center;
      text-overflow: ellipsis;
      font-size: $font-size-small;
      font-weight: $font-weight-bold;
      padding: 16px;
      flex: 1;
      height: 120px;
      cursor: pointer;
      transition: 200ms;
      border: 2px solid transparent;
      border-radius: $border-radius-root;
      // Real <button>s for keyboard access, so the user-agent font has to be overridden.
      font-family: inherit;
      appearance: none;

      &:focus-visible {
        outline: 2px solid $color-accent-500;
        outline-offset: 2px;
      }
    }

    .square-button.selected.go {
      border-color: $color-green;
    }

    .square-button.selected.nogo {
      border-color: $color-error;
    }

    .go {
      color: $color-green;
      background-color: $color-green-100;
    }

    .nogo {
      color: $color-error;
      background-color: $color-red-100;
    }
  }

  .close-icon__container {
    display: none;
  }

  .base-dialog__actions {
    display: flex;
    flex-direction: row;
    gap: 16px;
    padding: 16px;
    background: $color-white !important;
  }

  .base-dialog__header {
    width: 100%;
  }
}

.final-decision-dialog {
  &__title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  &__reasons {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 8px;
    padding: 16px 8px 0;
    max-width: 100%;
  }

  &__comment {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 8px 0;
    width: 100%;
  }

  &__comment-label {
    margin: 0;
    font-size: $font-size-small;
    font-weight: $font-weight-bold;
    color: $color-normal;
    text-align: left;
  }

  &__comment-hint {
    margin: 0;
    font-size: $font-size-small;
    color: $color-neutral;
    line-height: 1.4;
    text-align: left;
  }
}
</style>
